import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Badge } from '../../components/ui/badge';
import { Euro, MessagesSquare, ShoppingBag, Phone, Zap, User, Calendar } from 'lucide-react';
import { ResponsiveContainer, AreaChart, Area, BarChart, Bar, XAxis, YAxis, Tooltip, CartesianGrid } from 'recharts';
import PageHeader, { AccountSelector } from '../../components/PageHeader';
import { toast } from 'sonner';
import api, { apiError, fmtDateTime, parseUTC } from '../../lib/api';

const PERIODS = [
  { v: 'today', l: "Aujourd'hui" },
  { v: '7d', l: '7 jours' },
  { v: '14d', l: '14 jours' },
  { v: '30d', l: '30 jours' },
  { v: '90d', l: '90 jours' },
  { v: '365d', l: '1 an' },
  { v: 'all', l: 'Tout' },
];

export default function AccountTracking() {
  const [report, setReport] = useState(null);
  const [accounts, setAccounts] = useState([]);
  const [sales, setSales] = useState([]);
  const [account, setAccount] = useState('all');
  const [period, setPeriod] = useState('14d');
  const [series, setSeries] = useState([]);

  const load = useCallback(async () => {
    try {
      const [r, a, s] = await Promise.all([
        api.get('/reports/account-tracking'),
        api.get('/accounts'),
        api.get('/sales'),
      ]);
      setReport(r.data);
      setAccounts(a.data);
      setSales(s.data);
    } catch (e) { toast.error(apiError(e, 'Impossible de charger les données de suivi')); }
  }, []);

  useEffect(() => { load(); }, [load]);

  // Refresh conversations time-series when filters change
  useEffect(() => {
    const params = { period };
    if (account !== 'all') params.account_id = account;
    api.get('/reports/conversations-timeseries', { params }).then(({ data }) => setSeries(data)).catch(() => setSeries([]));
  }, [period, account]);

  const periodSinceMs = useMemo(() => {
    const now = Date.now();
    if (period === 'today') { const d = new Date(); d.setHours(0,0,0,0); return d.getTime(); }
    const m = period.match(/^(\d+)d$/);
    return m ? now - parseInt(m[1], 10) * 86400000 : null;
  }, [period]);

  const filteredSales = useMemo(() => sales.filter(s =>
    (account === 'all' || s.account_id === account) &&
    (!periodSinceMs || parseUTC(s.date).getTime() >= periodSinceMs)
  ), [sales, account, periodSinceMs]);

  const revenue = useMemo(() => filteredSales.filter(s => s.status === 'approved').reduce((sum, x) => sum + x.amount, 0), [filteredSales]);
  const approvedCount = useMemo(() => filteredSales.filter(s => s.status === 'approved').length, [filteredSales]);
  const newConvTotal = useMemo(() => series.reduce((s, x) => s + x.count, 0), [series]);

  // Adaptive revenue buckets so HISTORICAL imported sales are visible: group by
  // day for short ranges, by week up to ~6 months, by month beyond that.
  const revData = useMemo(() => {
    const approved = filteredSales.filter(s => s.status === 'approved');
    const times = approved.map(s => +parseUTC(s.date)).filter(t => !isNaN(t));
    if (!times.length) return { rows: [], gran: 'day' };
    const startMs = periodSinceMs != null ? periodSinceMs : Math.min(...times);
    const rangeDays = Math.max(1, Math.ceil((Date.now() - startMs) / 86400000));
    const gran = rangeDays <= 31 ? 'day' : rangeDays <= 180 ? 'week' : 'month';
    const keyOf = (t) => {
      const d = new Date(t);
      if (gran === 'month') return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
      if (gran === 'week') { const w = new Date(t); w.setDate(w.getDate() - ((w.getDay() + 6) % 7)); return w.toISOString().slice(0, 10); }
      return d.toISOString().slice(0, 10);
    };
    const buckets = new Map();
    approved.forEach(s => {
      const t = +parseUTC(s.date);
      if (isNaN(t) || t < startMs) return;
      const k = keyOf(t);
      buckets.set(k, (buckets.get(k) || 0) + s.amount);
    });
    const rows = [...buckets.entries()].sort((a, b) => (a[0] < b[0] ? -1 : 1))
      .map(([k, v]) => ({ label: gran === 'month' ? k.slice(2) : k.slice(5), total: Math.round(v) }));
    return { rows, gran };
  }, [filteredSales, periodSinceMs]);

  const chartData = useMemo(() => series.map(p => ({ ...p, label: p.date.slice(5) })), [series]);

  return (
    <div className="p-6 md:p-8 max-w-7xl mx-auto fade-in" data-testid="account-tracking-page">
      <PageHeader
        title="Suivi des comptes"
        subtitle="Revenus, conversations et ventes sur tes comptes Telegram"
        actions={<AccountSelector value={account} onChange={setAccount} accounts={accounts} allowAll />}
        onRefresh={load}
      />

      {/* Period filter */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-3 mb-4 flex items-center gap-2 flex-wrap" data-testid="period-bar">
        <Calendar size={14} className="text-slate-500 ml-1" />
        {PERIODS.map(p => (
          <button
            key={p.v}
            data-testid={`period-${p.v}`}
            onClick={() => setPeriod(p.v)}
            className={`px-3 h-8 rounded-lg text-xs font-medium transition-colors ${period === p.v ? 'bg-indigo-500 text-white' : 'bg-white/5 text-slate-300 hover:bg-white/10 border border-white/10'}`}
          >
            {p.l}
          </button>
        ))}
        <div className="ml-auto flex items-center gap-3 text-[11px] text-slate-500">
          <span><span className="text-emerald-400 font-bold tabular-nums">{revenue.toLocaleString()} €</span> de revenus</span>
          <span><span className="text-amber-300 font-bold tabular-nums">{approvedCount}</span> ventes</span>
          <span><span className="text-indigo-300 font-bold tabular-nums" data-testid="new-conv-total">{newConvTotal}</span> nouvelles convs</span>
        </div>
      </div>

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-3 mb-4">
        <Kpi testid="track-kpi-revenue" icon={Euro} color="text-emerald-400" label="Revenus approuvés" value={`${revenue.toLocaleString()} €`} />
        <Kpi testid="track-kpi-sales" icon={ShoppingBag} color="text-amber-400" label="Ventes" value={approvedCount} />
        <Kpi testid="track-kpi-convs" icon={MessagesSquare} color="text-indigo-400" label="Nouvelles convs" value={newConvTotal} />
        <Kpi testid="track-kpi-accounts" icon={Phone} color="text-violet-400" label="Comptes actifs" value={report?.accounts ?? '—'} />
      </div>

      {/* New conversations chart */}
      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-4" data-testid="conversations-chart">
        <div className="flex items-center justify-between mb-3 flex-wrap gap-2">
          <div>
            <h3 className="text-sm font-semibold text-white">Nouvelles conversations</h3>
            <p className="text-[11px] text-slate-500">{account === 'all' ? 'Tous les comptes' : (accounts.find(a => a.id === account)?.name || 'Compte')} · {period}</p>
          </div>
        </div>
        <div style={{ width: '100%', height: 220 }}>
          <ResponsiveContainer>
            <AreaChart data={chartData} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="trackConvFill" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="0%" stopColor="#6366f1" stopOpacity={0.45} />
                  <stop offset="100%" stopColor="#6366f1" stopOpacity={0} />
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
              <XAxis dataKey="label" stroke="#64748b" tick={{ fontSize: 10 }} />
              <YAxis allowDecimals={false} stroke="#64748b" tick={{ fontSize: 10 }} />
              <Tooltip
                contentStyle={{ background: '#0B0B0E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#e2e8f0', fontSize: 12 }}
                labelStyle={{ color: '#94a3b8' }}
              />
              <Area type="monotone" dataKey="count" stroke="#818cf8" strokeWidth={2} fill="url(#trackConvFill)" />
            </AreaChart>
          </ResponsiveContainer>
        </div>
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-5 mb-6" data-testid="revenue-chart-card">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm font-semibold text-white">Revenus — {period} <span className="text-[11px] font-normal text-slate-500">({revData.gran === 'month' ? 'par mois' : revData.gran === 'week' ? 'par semaine' : 'par jour'})</span></div>
          <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-300">approuvées uniquement</Badge>
        </div>
        {revData.rows.length === 0 ? (
          <div className="h-40 flex items-center justify-center text-sm text-slate-500" data-testid="revenue-chart-empty">Aucune vente approuvée sur cette période / ce compte.</div>
        ) : (
          <div style={{ width: '100%', height: 200 }} data-testid="revenue-chart">
            <ResponsiveContainer>
              <BarChart data={revData.rows} margin={{ top: 8, right: 8, left: -20, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="rgba(255,255,255,0.05)" />
                <XAxis dataKey="label" stroke="#64748b" tick={{ fontSize: 10 }} interval="preserveStartEnd" />
                <YAxis stroke="#64748b" tick={{ fontSize: 10 }} />
                <Tooltip
                  cursor={{ fill: 'rgba(255,255,255,0.04)' }}
                  contentStyle={{ background: '#0B0B0E', border: '1px solid rgba(255,255,255,0.1)', borderRadius: 8, color: '#e2e8f0', fontSize: 12 }}
                  labelStyle={{ color: '#94a3b8' }}
                  formatter={(v) => [`${Number(v).toLocaleString()} €`, 'Revenu']}
                />
                <Bar dataKey="total" fill="#8b5cf6" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>
        )}
      </div>

      <div className="rounded-2xl border border-white/10 bg-white/[0.02] overflow-hidden">
        <div className="px-5 py-3 border-b border-white/5 text-sm font-semibold text-white">Ventes récentes</div>
        <table className="w-full">
          <thead>
            <tr className="text-left text-xs uppercase tracking-wider text-slate-500 border-b border-white/5">
              <th className="px-5 py-2.5 font-semibold">Client</th>
              <th className="px-5 py-2.5 font-semibold">Compte</th>
              <th className="px-5 py-2.5 font-semibold">Type</th>
              <th className="px-5 py-2.5 font-semibold">Source</th>
              <th className="px-5 py-2.5 font-semibold">Date</th>
              <th className="px-5 py-2.5 font-semibold text-right">Montant</th>
            </tr>
          </thead>
          <tbody className="divide-y divide-white/5">
            {filteredSales.filter(s => s.status !== 'rejected' && s.status !== 'withdrawn').slice(0, 8).map(s => {
              const acc = accounts.find(a => a.id === s.account_id);
              return (
                <tr key={s.id} className="hover:bg-white/[0.02]">
                  <td className="px-5 py-2.5 text-sm text-white">{s.client}</td>
                  <td className="px-5 py-2.5 text-sm text-slate-300">{acc?.name || '—'}</td>
                  <td className="px-5 py-2.5"><Badge variant="outline" className="border-white/10 bg-white/5 text-slate-300 text-[10px]">{({ purchase: 'achat', donation: 'don', wishlist: 'wishlist', paid_chat: 'chat payant' })[s.type] || (s.type || '').replace('_', ' ')}</Badge></td>
                  <td className="px-5 py-2.5">{s.source === 'auto' ? <Zap size={12} className="text-amber-400" /> : <User size={12} className="text-indigo-400" />}</td>
                  <td className="px-5 py-2.5 text-xs text-slate-500">{fmtDateTime(s.date)}</td>
                  <td className="px-5 py-2.5 text-right text-sm font-bold text-emerald-400">{s.amount} €</td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
    </div>
  );
}

function Kpi({ icon: Icon, color, label, value, testid }) {
  return (
    <div className="rounded-2xl border border-white/10 bg-white/[0.02] p-4" data-testid={testid}>
      <Icon size={16} className={`${color} mb-2`} />
      <div className="text-xl font-bold text-white font-display">{value}</div>
      <div className="text-[11px] text-slate-500">{label}</div>
    </div>
  );
}
